/**
 * MobileNavSheet
 * ----------------
 * Minimal left-side slide-out drawer used by the mobile top bar.
 *
 * TODO(SWE-1 handoff): When `src/components/shared/ui/sheet.tsx` lands, delete
 * this file and import `Sheet`, `SheetContent`, `SheetTrigger` from shared/ui.
 * The external API of this component (`<MobileNavSheet open onOpenChange>...`)
 * is intentionally shaped so callers need no changes — they'll just swap the
 * import in `Nav.tsx`.
 *
 * Built on @radix-ui/react-dialog (already in deps) so we don't ship anything
 * custom-rolled for focus trapping / ESC / click-outside / a11y announcements.
 */
import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface MobileNavSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** Accessible title for screen readers. */
  title?: string;
}

const MobileNavSheet: React.FC<MobileNavSheetProps> = ({
  open,
  onOpenChange,
  children,
  title = "Navigation",
}) => (
  <Dialog.Root open={open} onOpenChange={onOpenChange}>
    <Dialog.Portal>
      <Dialog.Overlay
        className={cn(
          "fixed inset-0 z-40 bg-black/40 backdrop-blur-sm",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
        )}
      />
      <Dialog.Content
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col",
          "bg-background border-r border-border shadow-xl",
          "focus:outline-none",
          "data-[state=open]:animate-in data-[state=open]:slide-in-from-left",
          "data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left",
        )}
        aria-label={title}
      >
        <Dialog.Title className="sr-only">{title}</Dialog.Title>
        <Dialog.Close
          aria-label="Close navigation"
          className={cn(
            "absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center",
            "rounded-md text-muted-foreground hover:text-foreground hover:bg-muted",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <XIcon size={18} />
        </Dialog.Close>
        {children}
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
);

export default MobileNavSheet;
